import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import COUNTRIES, { TOP_COUNT } from '../../common/countries';
import { loadStates, loadLgas, suggestedCountry, countryName } from '../../common/geo';
import { flag, data } from '../../common/community';
import TAXONOMY from '../../common/taxonomy';
import { forgetInvite } from '../../common/invite';
const PAGE = 10; // options shown before searching; everything else is found by typing

/**
 * Step-by-step, searchable setup (like the tag picker), for up to three places:
 * where you live now, (abroad) your home in Nigeria, and your state of origin.
 * attrs: { location (existing, for editing), onfinish(location) }
 */
export default class CommunitySetup extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    const l = this.attrs.location || {};
    this.f = {
      country: l.country || '',
      city: l.city || '',
      resStateId: l.resStateId || null,
      resLgaId: l.resLgaId || null,
      originStateId: l.originStateId || null,
      originLgaId: l.originLgaId || null,
      showLocation: l.showLocation ?? true,
      showOrigin: l.showOrigin ?? true
    };
    const p = this.attrs.profile || {};
    Object.assign(this.f, {
      profession: p.profession || null,
      professionOther: p.professionOther || '',
      industry: p.industry || null,
      study: p.study || null,
      studyOther: p.studyOther || '',
      showProfession: p.showProfession ?? true
    });
    this.suggested = l.country ? null : suggestedCountry();
    // Settings can open straight at the profession questions; invited first-timers start with the invite
    this.invite = !l.country ? this.attrs.invite || null : null;
    this.skip = {};
    this.connection = null;
    this.step = this.attrs.start === 'profession' && l.country ? 'profession' : this.invite ? 'invite' : 'country';
    this.history = [];
    this.query = '';
    this.showAll = false;
    this.states = null;
    this.lgas = {};
    this.saving = false;
    this.error = null;
    loadStates().then(s => this.states = s).catch(() => this.error = "Couldn't load the list of states. Please refresh the page.").finally(() => m.redraw());
  }

  // ---------- navigation ----------
  go(step) {
    // Steps already answered by the invite are skipped (no asking twice)
    const AFTER = {
      resState: 'resLga',
      resLga: 'origin',
      homeState: 'homeLga',
      homeLga: 'origin',
      origin: 'originLga',
      originLga: 'profession'
    };
    while (this.skip[step] && AFTER[step]) step = AFTER[step];
    this.history.push(this.step);
    this.step = step;
    this.query = '';
    this.showAll = false;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  back() {
    this.step = this.history.pop() || 'country';
    this.query = '';
    this.showAll = false;
  }
  inNigeria() {
    return this.f.country === 'NG';
  }
  stateName(id) {
    return (this.states || []).find(s => s.id === id)?.name || '';
  }
  lgaList(stateId) {
    if (!stateId) return null;
    if (!this.lgas[stateId]) {
      this.lgas[stateId] = 'loading';
      loadLgas(stateId).then(l => {
        this.lgas[stateId] = l;
        m.redraw();
      });
    }
    return this.lgas[stateId] === 'loading' ? null : this.lgas[stateId];
  }

  // ---------- the searchable card grid ----------
  /** A privacy setting: the whole row is the switch (role="switch" for screen readers). */
  toggle(key, icon, title, desc) {
    const on = !!this.f[key];
    return m("button", {
      type: "button",
      role: "switch",
      "aria-checked": on ? 'true' : 'false',
      className: 'VouchaToggle' + (on ? ' is-on' : ''),
      onclick: () => this.f[key] = !on
    }, m("i", {
      className: icon + ' VouchaToggle-icon',
      "aria-hidden": "true"
    }), m("span", {
      className: "VouchaToggle-text"
    }, m("strong", null, title), m("small", null, on ? desc : 'Hidden from your profile')), m("span", {
      className: "VouchaToggle-switch",
      "aria-hidden": "true"
    }, m("span", null)));
  }
  picker({
    title,
    text,
    items,
    onpick,
    custom,
    skip,
    selected
  }) {
    const q = this.query.trim().toLowerCase();
    const matches = q ? items.filter(i => (i.label + ' ' + (i.sub || '')).toLowerCase().includes(q)) : items;
    const shown = q ? matches : matches.slice(0, PAGE);
    return m("div", {
      className: "VouchaSetup-step"
    }, m("h2", null, title), text ? m("p", {
      className: "VouchaSetup-text"
    }, text) : null, m("div", {
      className: "VouchaSetup-search"
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl",
      placeholder: "Search",
      value: this.query,
      oninput: e => {
        this.query = e.target.value;
      },
      oncreate: v => v.dom.focus({
        preventScroll: true
      })
    })), m("div", {
      className: "VouchaSetup-grid"
    }, custom && q && !matches.some(i => i.label.toLowerCase() === q) ? m("button", {
      className: "VouchaSetup-card is-custom",
      onclick: () => custom(this.query.trim())
    }, m("span", {
      className: "VouchaSetup-cardIcon"
    }, m("i", {
      className: "fas fa-plus"
    })), m("span", {
      className: "VouchaSetup-cardBody"
    }, m("strong", null, "Use \u201C", this.query.trim(), "\u201D"))) : null, shown.map(i => m("button", {
      className: 'VouchaSetup-card' + (selected === i.id ? ' is-selected' : ''),
      onclick: () => onpick(i)
    }, m("span", {
      className: "VouchaSetup-cardIcon"
    }, i.icon), m("span", {
      className: "VouchaSetup-cardBody"
    }, m("strong", null, i.label), i.sub ? m("small", null, i.sub) : null)))), !q && matches.length > PAGE ? m("p", {
      className: "VouchaSetup-hint"
    }, m("i", {
      className: "fas fa-search"
    }), " Showing ", PAGE, " of ", matches.length, ". Type above to find the others.") : null, q && !matches.length && !custom ? m("p", {
      className: "VouchaSetup-text"
    }, "No matches. Try another spelling.") : null, m("div", {
      className: "VouchaSetup-nav"
    }, this.history.length ? m(Button, {
      className: "Button Button--link",
      icon: "fas fa-arrow-left",
      onclick: () => this.back()
    }, "Back") : m("span", null), skip ? m(Button, {
      className: "Button Button--link",
      onclick: skip
    }, "Skip") : null));
  }
  inviteStep() {
    const p = this.invite.place;
    const choose = connection => {
      const f = this.f;
      this.connection = connection;
      this.skip = {};
      const lga = p.lgaId || null;
      if (connection === 'from' || connection === 'both') {
        f.originStateId = p.stateId;
        f.originLgaId = lga;
        this.skip.origin = true;
        if (lga) this.skip.originLga = true;
      }
      if (connection === 'live' || connection === 'both') {
        f.country = 'NG';
        f.resStateId = p.stateId;
        f.resLgaId = lga;
        this.skip.resState = true;
        if (lga) this.skip.resLga = true;
      }
      if (p.stateId) this.lgaList(p.stateId); // so the summary can name the LGA
      this.go(connection === 'live' || connection === 'both' ? 'resState' : 'country');
    };
    const option = (key, icon, label) => m("button", {
      className: "VouchaSetup-card",
      onclick: () => choose(key)
    }, m("span", {
      className: "VouchaSetup-cardIcon"
    }, m("i", {
      className: icon
    })), m("span", {
      className: "VouchaSetup-cardBody"
    }, m("strong", null, label)));
    return m("div", {
      className: "VouchaSetup-step"
    }, m("h2", null, "How are you connected to ", p.name, "?"), m("p", {
      className: "VouchaSetup-text"
    }, "You were invited to the ", p.name, " community. This sets it up for you; you'll add the rest next."), m("div", {
      className: "VouchaSetup-grid"
    }, option('from', 'fas fa-seedling', "I'm from " + p.name), option('live', 'fas fa-map-marker-alt', 'I live in ' + p.name), option('both', 'fas fa-home', 'Both: from there and living there'), option('neither', 'fas fa-times', "Neither, I'll choose my own places")));
  }
  stateItems() {
    return (this.states || []).map(s => ({
      id: s.id,
      label: s.name,
      sub: s.capital + ' · ' + s.zone,
      icon: m("i", {
        className: "fas fa-map"
      })
    }));
  }
  lgaItems(stateId) {
    return (this.lgaList(stateId) || []).map(l => ({
      id: l.id,
      label: l.name,
      icon: m("i", {
        className: "fas fa-map-pin"
      })
    }));
  }

  // ---------- steps ----------
  view() {
    if (!this.states && !this.error) return m("div", {
      className: "VouchaSetup"
    }, m(LoadingIndicator, null));
    const f = this.f;
    const work = ['profession', 'industry', 'study'];
    const steps = this.inNigeria() ? ['country', 'resState', 'resLga', 'origin', 'originLga', ...work, 'privacy'] : ['country', 'city', 'homeState', 'homeLga', 'origin', 'originLga', ...work, 'privacy'];
    if (this.invite) steps.unshift('invite');
    const at = Math.max(0, steps.indexOf(this.step));
    return m("div", {
      className: "VouchaSetup"
    }, m("div", {
      className: "VouchaSetup-progress"
    }, m("span", {
      style: {
        width: Math.round((at + 1) / steps.length * 100) + '%'
      }
    })), this.error ? m("p", {
      className: "VouchaSetup-error"
    }, this.error) : null, this.stepView());
  }
  stepView() {
    const f = this.f;
    switch (this.step) {
      case 'invite':
        return this.inviteStep();
      case 'country':
        {
          const items = COUNTRIES.map(([code, name], i) => ({
            id: code,
            label: name,
            icon: m("span", {
              className: "VouchaSetup-flag"
            }, flag(code)),
            top: i < TOP_COUNT
          }));
          return this.picker({
            title: 'Where do you live now?',
            text: this.suggested ? 'Your device suggests ' + countryName(this.suggested) + '. Tap it, or search for another country.' : 'Search for your country.',
            items: this.suggested ? [items.find(i => i.id === this.suggested), ...items.filter(i => i.id !== this.suggested)] : items,
            selected: f.country,
            onpick: i => {
              if (i.id !== f.country) {
                f.city = '';
                if (i.id === 'NG' || f.country === 'NG') {
                  f.resStateId = null;
                  f.resLgaId = null;
                }
              }
              f.country = i.id;
              this.go(i.id === 'NG' ? 'resState' : 'city');
            }
          });
        }
      case 'city':
        {
          // City tags for this country, sent with the page (found by slug or name on the server)
          const cities = ((data().cities || {})[f.country] || {}).cities || [];
          return this.picker({
            title: 'Which city?',
            text: cities.length ? 'Pick your city, or type it if it isn’t listed.' : 'Type your city.',
            items: cities.map(c => ({
              id: c.name,
              label: c.name,
              sub: countryName(f.country),
              icon: m("i", {
                className: "fas fa-city"
              })
            })),
            selected: f.city,
            custom: typed => {
              f.city = typed;
              this.go('homeState');
            },
            onpick: i => {
              f.city = i.label;
              this.go('homeState');
            },
            skip: () => {
              f.city = '';
              this.go('homeState');
            }
          });
        }
      case 'resState':
        return this.picker({
          title: 'Which state do you live in?',
          items: this.stateItems(),
          selected: f.resStateId,
          onpick: i => {
            if (i.id !== f.resStateId) f.resLgaId = null;
            f.resStateId = i.id;
            this.go('resLga');
          }
        });
      case 'homeState':
        return this.picker({
          title: 'Where is home in Nigeria?',
          text: 'The state where your family home is, or where you lived before moving abroad.',
          items: this.stateItems(),
          selected: f.resStateId,
          onpick: i => {
            if (i.id !== f.resStateId) f.resLgaId = null;
            f.resStateId = i.id;
            this.go('homeLga');
          },
          skip: () => {
            f.resStateId = null;
            f.resLgaId = null;
            this.go('origin');
          }
        });
      case 'resLga':
      case 'homeLga':
        return this.lgaList(f.resStateId) === null ? m("div", {
          className: "VouchaSetup"
        }, m(LoadingIndicator, null)) : this.picker({
          title: 'Which LGA in ' + this.stateName(f.resStateId) + '?',
          items: this.lgaItems(f.resStateId),
          selected: f.resLgaId,
          onpick: i => {
            f.resLgaId = i.id;
            this.go('origin');
          },
          skip: () => {
            f.resLgaId = null;
            this.go('origin');
          }
        });
      case 'origin':
        {
          const same = f.resStateId ? [{
            id: 'same',
            label: 'Same as ' + (this.inNigeria() ? 'where I live' : 'my home'),
            sub: this.stateName(f.resStateId),
            icon: m("i", {
              className: "fas fa-equals"
            })
          }] : [];
          return this.picker({
            title: 'What is your state of origin?',
            items: [...same, ...this.stateItems()],
            selected: f.originStateId,
            onpick: i => {
              if (i.id === 'same') {
                f.originStateId = f.resStateId;
                f.originLgaId = f.resLgaId;
                this.go('profession');
                return;
              }
              if (i.id !== f.originStateId) f.originLgaId = null;
              f.originStateId = i.id;
              this.go('originLga');
            },
            skip: () => {
              f.originStateId = null;
              f.originLgaId = null;
              this.go('profession');
            }
          });
        }
      case 'originLga':
        return this.lgaList(f.originStateId) === null ? m("div", {
          className: "VouchaSetup"
        }, m(LoadingIndicator, null)) : this.picker({
          title: 'Which LGA in ' + this.stateName(f.originStateId) + '?',
          items: this.lgaItems(f.originStateId),
          selected: f.originLgaId,
          onpick: i => {
            f.originLgaId = i.id;
            this.go('profession');
          },
          skip: () => {
            f.originLgaId = null;
            this.go('profession');
          }
        });
      case 'profession':
        return this.picker({
          title: 'What do you add to your community?',
          text: 'Your profession. It helps show the skills each community has (only as totals; never a list of names).',
          items: TAXONOMY.professions.map(x => ({
            id: x.key,
            label: x.name,
            sub: x.group,
            icon: m("i", {
              className: "fas fa-briefcase"
            })
          })),
          selected: f.profession,
          custom: typed => {
            f.profession = null;
            f.professionOther = typed;
            this.go('industry');
          },
          onpick: i => {
            f.profession = i.id;
            f.professionOther = '';
            this.go('industry');
          },
          skip: () => this.go('industry')
        });
      case 'industry':
        return this.picker({
          title: 'Which industry do you work in?',
          items: TAXONOMY.industries.map(x => ({
            id: x.key,
            label: x.name,
            icon: m("i", {
              className: "fas fa-industry"
            })
          })),
          selected: f.industry,
          onpick: i => {
            f.industry = i.id;
            this.go('study');
          },
          skip: () => this.go('study')
        });
      case 'study':
        return this.picker({
          title: 'What did you study?',
          items: TAXONOMY.studies.map(x => ({
            id: x.key,
            label: x.name,
            sub: x.group,
            icon: m("i", {
              className: "fas fa-graduation-cap"
            })
          })),
          selected: f.study,
          custom: typed => {
            f.study = null;
            f.studyOther = typed;
            this.go('privacy');
          },
          onpick: i => {
            f.study = i.id;
            f.studyOther = '';
            this.go('privacy');
          },
          skip: () => this.go('privacy')
        });
      default:
        return this.privacyStep();
    }
  }
  privacyStep() {
    const f = this.f;
    const places = [[flag(f.country), f.country === 'NG' ? [this.lgaName(f.resStateId, f.resLgaId), this.stateName(f.resStateId)].filter(Boolean).join(', ') || 'Nigeria' : [f.city, countryName(f.country)].filter(Boolean).join(', '), 'Where you live'], f.country !== 'NG' && f.resStateId ? ['🏠', [this.lgaName(f.resStateId, f.resLgaId), this.stateName(f.resStateId)].filter(Boolean).join(', '), 'Home in Nigeria'] : null, f.originStateId ? ['🌱', [this.lgaName(f.originStateId, f.originLgaId), this.stateName(f.originStateId)].filter(Boolean).join(', '), 'State of origin'] : null, this.professionText() ? ['💼', this.professionText(), 'What you do'] : null, this.studyText() ? ['🎓', this.studyText(), 'What you studied'] : null].filter(Boolean);
    return m("div", {
      className: "VouchaSetup-step"
    }, m("h2", null, "Your community"), m("ul", {
      className: "VouchaSetup-summary"
    }, places.map(([icon, text, label]) => m("li", null, m("span", {
      className: "VouchaSetup-flag"
    }, icon), m("span", null, m("small", null, label), m("strong", null, text))))), m("div", {
      className: "VouchaSetup-switches"
    }, this.toggle('showLocation', 'fas fa-map-marker-alt', 'Where I live', 'Shown on your profile'), this.toggle('showOrigin', 'fas fa-seedling', 'My state of origin', 'Shown on your profile'), this.toggle('showProfession', 'fas fa-briefcase', 'My profession and studies', 'Shown on your profile')), m("div", {
      className: "VouchaSetup-nav"
    }, m(Button, {
      className: "Button Button--link",
      icon: "fas fa-arrow-left",
      onclick: () => this.back()
    }, "Back"), m(Button, {
      className: "Button Button--primary",
      loading: this.saving,
      onclick: () => this.finish()
    }, "Show my community")));
  }
  professionText() {
    const f = this.f;
    const p = TAXONOMY.professions.find(x => x.key === f.profession);
    const i = TAXONOMY.industries.find(x => x.key === f.industry);
    return [p ? p.name : f.professionOther, i ? i.name : null].filter(Boolean).join(' · ');
  }
  studyText() {
    const s = TAXONOMY.studies.find(x => x.key === this.f.study);
    return s ? s.name : this.f.studyOther;
  }
  lgaName(stateId, lgaId) {
    const list = this.lgas[stateId];
    return Array.isArray(list) ? list.find(l => l.id === lgaId)?.name || '' : '';
  }
  async finish() {
    this.saving = true;
    this.error = null;
    m.redraw();
    try {
      const body = this.invite ? {
        ...this.f,
        inviteCode: this.invite.code,
        inviteConnection: this.connection || 'neither'
      } : this.f;
      const r = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/voucha-community/me',
        body,
        errorHandler: () => {}
      });
      if (this.invite) forgetInvite();
      if (this.attrs.onfinish) await this.attrs.onfinish(r.location);
    } catch (e) {
      this.error = e?.response?.message || "That didn't save. Please try again.";
    }
    this.saving = false;
    m.redraw();
  }
}
flarum.reg.add('voucha-community', 'forum/components/CommunitySetup', CommunitySetup);